/**
 * Gift a Moment — Voice step.
 *
 * Two modes:
 *   1. Use Michael's cloned voice (instant — ElevenLabs TTS at preview time)
 *   2. Record yourself — display the script and capture the sender's own
 *      voice with MediaRecorder. On success the webm blob is uploaded to
 *      /api/gift/{id}/record and the backend mixes it with the music bed.
 *
 * The user-recorded path is the emotionally strongest — the recipient
 * hears the actual voice of the person who cared enough to make this.
 */
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, Mic, Play, Pause, Square, RotateCcw, Sparkles, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const VIOLET = "#8B7ED4";
const GOLD = "#CBA153";
const MAX_RECORDING_MS = 6 * 60 * 1000; // 6 minutes

const token = () => { try { return localStorage.getItem("reclaim_token") || ""; } catch (_) { return ""; } };

const fmtTime = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const ROLE_STYLES = {
  opening: { eyebrow: "Opening", accent: `${VIOLET}aa` },
  personal_intro: { eyebrow: "Personal message", accent: GOLD },
  personal: { eyebrow: "Personal message", accent: GOLD },
  affirmation: { eyebrow: "Affirmations", accent: VIOLET },
  closing: { eyebrow: "Closing", accent: `${VIOLET}aa` },
};

// ─── Voice choice sub-screen ────────────────────
const VoiceChoice = ({ onMichael, onRecord }) => (
  <motion.div key="voice-choice" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }} className="px-6 pt-6 pb-24" data-testid="gift-step-voice">
    <div className="max-w-md mx-auto text-center mb-10">
      <p className="text-[10px] uppercase tracking-[0.4em] mb-4" style={{ color: `${VIOLET}bb` }}>Step 6 of 7</p>
      <h1 className="text-3xl mb-3" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.94)" }}>
        Whose voice carries this?
      </h1>
      <p className="text-sm italic px-4" style={{ fontFamily: "'Spectral', serif", color: "rgba(220,210,230,0.55)" }}>
        The most moving gifts are the ones where they can hear <em>you</em>.
      </p>
    </div>

    <div className="max-w-md mx-auto space-y-3">
      {/* Record yourself — emphasised */}
      <button onClick={onRecord} data-testid="gift-voice-record"
              className="w-full text-left rounded-3xl border-2 px-6 py-5 transition-all"
              style={{ background: `linear-gradient(135deg, ${VIOLET}22 0%, ${GOLD}12 100%)`,
                       borderColor: `${VIOLET}88` }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center"
               style={{ background: `${VIOLET}44`, border: `1px solid ${VIOLET}` }}>
            <Mic size={20} color="white" />
          </div>
          <div className="flex-1">
            <p className="text-lg" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.95)" }}>
              Record it in your own voice
            </p>
            <p className="text-xs mt-1" style={{ color: "rgba(220,210,230,0.65)" }}>
              Recommended · the most personal thing you can send
            </p>
          </div>
        </div>
      </button>

      <button onClick={onMichael} data-testid="gift-voice-michael"
              className="w-full text-left rounded-3xl border px-6 py-5 transition-all"
              style={{ background: "rgba(255,255,255,0.025)", borderColor: `${VIOLET}1f` }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center"
               style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Sparkles size={18} color={`${VIOLET}cc`} />
          </div>
          <div className="flex-1">
            <p className="text-lg" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.92)" }}>
              Use Michael&apos;s cloned voice
            </p>
            <p className="text-xs mt-1" style={{ color: "rgba(220,210,230,0.55)" }}>
              Instant · Reclaim signature narration
            </p>
          </div>
        </div>
      </button>
    </div>
  </motion.div>
);

// ─── Record screen ──────────────────────────────
const RecordScreen = ({ gift, onBack, onUploaded }) => {
  const [script, setScript] = useState([]);
  const [state, setState] = useState("idle"); // idle|requesting|recording|recorded|uploading
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const blobRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const mimeRef = useRef("audio/webm");
  const extRef = useRef("webm");

  useEffect(() => {
    fetch(`${API}/api/gift/${gift.gift_id}/script`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.json()).then((d) => setScript(d.script || []))
      .catch(() => setError("Could not load your script."));
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    };
  }, [gift.gift_id]);

  const start = async () => {
    setError(null);
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });
      streamRef.current = stream;

      let mime = "";
      if (typeof MediaRecorder !== "undefined") {
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) { mime = "audio/webm;codecs=opus"; extRef.current = "webm"; }
        else if (MediaRecorder.isTypeSupported("audio/webm")) { mime = "audio/webm"; extRef.current = "webm"; }
        else if (MediaRecorder.isTypeSupported("audio/mp4")) { mime = "audio/mp4"; extRef.current = "mp4"; }
      }
      mimeRef.current = mime || "audio/webm";

      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      recRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || mimeRef.current || "audio/webm" });
        blobRef.current = blob;
        if (blobUrl) URL.revokeObjectURL(blobUrl);
        setBlobUrl(URL.createObjectURL(blob));
        setState("recorded");
        if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
      };
      rec.start(200);
      setState("recording");
      setElapsed(0);
      const startedAt = Date.now();
      tickRef.current = setInterval(() => {
        const e = Date.now() - startedAt;
        setElapsed(e);
        if (e >= MAX_RECORDING_MS) stop();
      }, 250);
    } catch (e) {
      setError(e?.message === "Permission denied" || e?.name === "NotAllowedError"
        ? "Microphone access was blocked. Please allow mic access and try again."
        : "Could not start recording. Please check your microphone.");
      setState("idle");
    }
  };

  const stop = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (recRef.current && recRef.current.state !== "inactive") {
      try { recRef.current.stop(); } catch (_) {}
    }
  };

  const redo = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    blobRef.current = null;
    setBlobUrl(null);
    setElapsed(0);
    setState("idle");
  };

  const upload = async () => {
    if (!blobRef.current) return;
    setState("uploading");
    setError(null);
    try {
      const fd = new FormData();
      const ext = extRef.current || "webm";
      fd.append("file", blobRef.current, `recording.${ext}`);
      const r = await fetch(`${API}/api/gift/${gift.gift_id}/record`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      if (!r.ok) {
        let detail = "";
        try { detail = (await r.json())?.detail || ""; } catch (_) {}
        throw new Error(detail || `${r.status}`);
      }
      const updated = await r.json();
      onUploaded(updated);
    } catch (e) {
      setError(`We couldn't save your recording. (${e.message || "server error"})`);
      setState("recorded");
    }
  };

  return (
    <motion.div key="record" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5 }} className="px-6 pt-4 pb-24" data-testid="gift-record-screen">
      <div className="max-w-md mx-auto text-center mb-6">
        <p className="text-[10px] uppercase tracking-[0.4em] mb-3" style={{ color: `${VIOLET}bb` }}>Record your voice</p>
        <h1 className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.94)" }}>
          Read slowly, from the heart
        </h1>
        <p className="text-xs italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(220,210,230,0.55)" }}>
          Don&apos;t worry about being perfect. Pauses are beautiful. Music plays underneath afterwards.
        </p>
      </div>

      {/* Script */}
      <div className="max-w-md mx-auto rounded-3xl border p-5 mb-6"
           style={{ background: "rgba(255,255,255,0.03)", borderColor: `${VIOLET}2a` }}
           data-testid="gift-script-block">
        {script.length === 0 ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="animate-spin" color={VIOLET} /></div>
        ) : (
          <>
            {(() => {
              const blocks = [];
              let curRole = null;
              script.forEach((line, i) => {
                if (line.role !== curRole) {
                  const r = ROLE_STYLES[line.role] || ROLE_STYLES.opening;
                  blocks.push(
                    <p key={`h-${i}`} className="text-[10px] uppercase tracking-[0.3em] mt-4 first:mt-0 mb-2"
                       style={{ color: r.accent }}>{r.eyebrow}</p>
                  );
                  curRole = line.role;
                }
                blocks.push(
                  <p key={`l-${i}`} className="text-[15px] mb-1.5"
                     style={{ fontFamily: line.role === "affirmation" ? "'Playfair Display', serif" : "'Spectral', serif",
                              color: "rgba(245,240,230,0.92)",
                              fontStyle: line.role === "personal" ? "italic" : "normal" }}>
                    {line.text}
                  </p>
                );
              });
              return blocks;
            })()}
          </>
        )}
      </div>

      {error && (
        <div className="max-w-md mx-auto mb-4 px-4 py-2 rounded-lg text-xs" data-testid="gift-record-error"
             style={{ background: "rgba(255,80,80,0.1)", color: "#ffb3b3", border: "1px solid rgba(255,80,80,0.25)" }}>
          {error}
        </div>
      )}

      {/* Controls */}
      <div className="max-w-md mx-auto space-y-3">
        {state === "idle" && (
          <button onClick={start} data-testid="gift-record-start"
                  className="w-full rounded-full py-4 text-sm tracking-[0.3em] uppercase flex items-center justify-center gap-2"
                  style={{ background: `linear-gradient(135deg, ${VIOLET} 0%, #6A5EB0 100%)`, color: "white",
                           fontFamily: "'Manrope', sans-serif", fontWeight: 500,
                           boxShadow: `0 8px 40px ${VIOLET}44` }}>
            <Mic size={16} /> Start recording
          </button>
        )}

        {state === "requesting" && (
          <div className="text-center text-sm py-4" style={{ color: "rgba(220,210,230,0.65)" }}>
            <Loader2 size={16} className="animate-spin inline mr-2" /> Requesting microphone…
          </div>
        )}

        {state === "recording" && (
          <button onClick={stop} data-testid="gift-record-stop"
                  className="w-full rounded-full py-4 text-sm tracking-[0.3em] uppercase flex items-center justify-center gap-2"
                  style={{ background: "rgba(220,60,80,0.8)", color: "white", fontFamily: "'Manrope', sans-serif" }}>
            <Square size={14} /> Stop · {fmtTime(elapsed)} <span style={{ opacity: 0.6 }}>/ {fmtTime(MAX_RECORDING_MS)}</span>
          </button>
        )}

        {state === "recorded" && blobUrl && (
          <>
            <div className="rounded-2xl border p-3 flex items-center gap-2"
                 style={{ background: "rgba(255,255,255,0.035)", borderColor: `${VIOLET}2f` }}>
              <audio src={blobUrl} controls className="flex-1" data-testid="gift-record-preview" />
            </div>
            <button onClick={upload} data-testid="gift-record-upload"
                    className="w-full rounded-full py-4 text-sm tracking-[0.3em] uppercase flex items-center justify-center gap-2"
                    style={{ background: `linear-gradient(135deg, ${VIOLET} 0%, #6A5EB0 100%)`, color: "white",
                             fontFamily: "'Manrope', sans-serif", fontWeight: 500 }}>
              <Upload size={15} /> Use this recording
            </button>
            <button onClick={redo} data-testid="gift-record-redo"
                    className="w-full rounded-full border py-3 text-[11px] uppercase tracking-[0.32em] flex items-center justify-center gap-2"
                    style={{ background: "transparent", borderColor: `${VIOLET}44`, color: "rgba(220,210,230,0.85)", fontFamily: "'Manrope', sans-serif" }}>
              <RotateCcw size={13} /> Record again
            </button>
          </>
        )}

        {state === "uploading" && (
          <div className="text-center text-sm py-6" style={{ color: "rgba(220,210,230,0.75)" }}>
            <Loader2 size={18} className="animate-spin inline mr-2" color={VIOLET} />
            Mixing your voice with the music…
          </div>
        )}

        <button onClick={onBack} data-testid="gift-record-back"
                className="w-full rounded-full py-3 text-[11px] uppercase tracking-[0.3em]"
                style={{ background: "transparent", color: "rgba(180,170,200,0.6)", fontFamily: "'Manrope', sans-serif" }}>
          Back to voice choice
        </button>
      </div>
    </motion.div>
  );
};

export { VoiceChoice, RecordScreen };
